'use client';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faCartShopping, faHeart } from '@fortawesome/pro-solid-svg-icons';
import { sendGTMEvent } from '@next/third-parties/google';
import { getTestId, ProductTestIds } from '@shared/index';
import { BreadcrumbItem, Breadcrumbs, Button, Gallery, Icon, IconButton, Link } from '@ui';
import { LazyMotion, m } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';
import QuantityInput from '@/components/common/quantity-input/quantity-input';
import { useCookiebotConsent } from '@/hooks/use-cookiebot-consent';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import { useGetDiscountType } from '@/hooks/use-has-discount';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import useProductAverageRating from '@/hooks/use-product-average-rating';
import useProductDetailPageContext, {
  useReviewProductIds,
} from '@/hooks/use-product-detail-page-context';
import { useSentinelInView } from '@/hooks/use-sentinel-in-view';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import { createTrackingEventId } from '@/utils/capi-event';
import { postCapiProductPageView } from '@/utils/capi-tracking';
import loadFramerMotionFeatures from '@/utils/framer-motion-features';
import { getCategoryId } from '@/utils/get-category-id';
import FeatureFlag from '../common/feature-flag/feature-flag';
import FormattedPrice from '../common/formatted-price/formatted-price';
import GeneralInfo from './general-info/general-info';
import ProductDetailTabs from './product-detail-tabs/product-detail-tabs';
import RatingStars from './rating-stars/rating-stars';
import SimilarProducts from './similar-products/similar-products';
import StickyHeader from './sticky-header/sticky-header';
import { getBreadcrumbs } from './utils';
import VariantSelector from './variant-selector/variant-selector';

const ProductDetail: FC = () => {
  const { t, tFormat } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { favorites, addProductToFavorites, removeProductFromFavorites, isFavoritePending } =
    useFavorites();
  const { features, currency } = useCoreConfig();
  const { getDiscountType } = useGetDiscountType();
  const { addProductToMyCart, isPendingUpdateMyCartProductId } = useMyCart();

  const { product } = useProductDetailPageContext();
  const parentCategoryId = getCategoryId(product.superCategories.map(category => category.id));
  const reviewProductIds = useReviewProductIds();
  const { average: averageRating, count: reviewCount } = useProductAverageRating(reviewProductIds);

  const breadcrumbs = getBreadcrumbs(product);
  const productsPageLabel = t?.products.productsPage ?? 'Products';
  const isInFavorites = !!favorites.products.find(p => p.id === product.id);

  const ref = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  const isVisibleDesktop = useSentinelInView(ref, product.id);
  const isVisibleMobile = useSentinelInView(mobileRef, product.id);
  const [quantity, setQuantity] = useState(1);

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;
  const hasMarketingConsent = useCookiebotConsent('marketing');
  const shouldShowPrice = (!isAuthenticated && features.priceForGuests) || isAuthenticated;

  useEffect(() => {
    if (isStudioPreview) return;

    const eventId = createTrackingEventId();
    const sourceUrl = window.location.href;

    sendGTMEvent({ ecommerce: null });
    sendGTMEvent({
      event: 'view_item',
      event_id: eventId,
      ecommerce: {
        currency,
        value: product.discountedPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            price: product.discountedPrice,
            discount: product.discountedPrice ? product.price - product.discountedPrice : 0,
            item_category: product.superCategories?.[0]?.name || '',
          },
        ],
      },
    });

    if (hasMarketingConsent && product.id) {
      void postCapiProductPageView({
        id: eventId,
        sourceUrl,
        productId: product.id,
      });
    }
  }, [hasMarketingConsent, isStudioPreview, product, currency]);

  return (
    // `LazyMotion` + `m.*` defers framer-motion's heavy projection layer
    // until any `m.*` child mounts. The async features loader (see
    // `@/utils/framer-motion-features`) lets webpack split the animation
    // features into their own chunk, loaded after first paint.
    <LazyMotion features={loadFramerMotionFeatures} strict>
      <div>
        {isAuthenticated && (
          <StickyHeader isVisibleDesktop={isVisibleDesktop} isVisibleMobile={isVisibleMobile} />
        )}
        <Breadcrumbs className="mb-4">
          <BreadcrumbItem>
            <Link noStyle href="/products">
              {productsPageLabel}
            </Link>
          </BreadcrumbItem>
          {breadcrumbs.map(item => (
            <BreadcrumbItem key={item.url}>
              <Link href={item.url} noStyle>
                {item.label}
              </Link>
            </BreadcrumbItem>
          ))}
          <BreadcrumbItem>{product?.name || t?.productDetail.details}</BreadcrumbItem>
        </Breadcrumbs>
        <div
          className="bg-content1 border-default-200 flex flex-col gap-6 rounded-2xl border p-8 pr-6 shadow-sm @lg:flex-row @lg:gap-20"
          ref={mobileRef}
        >
          <Gallery product={{ ...product, discountedPriceType: 'NONE', parentCategoryId: '' }} />
          <div className="flex w-full flex-col">
            {/* <div className="flex flex-col gap-1 md:flex-row">
            <span className="text-sm font-light">{t?.productDetail.productCod}</span>
            <span className="text-sm font-normal">{product.sku}</span>
          </div> */}

            <h1 className="text-2xl font-bold @lg:text-4xl">{product.name}</h1>
            <div className={cn(shouldShowPrice ? 'mt-3' : 'mt-0 hidden')}>
              {(() => {
                const discountType = getDiscountType(product, parentCategoryId, product.id);

                if (discountType === 'NONE') {
                  return (
                    <FormattedPrice
                      size="3xl"
                      price={product.price}
                      productId={product.id}
                      showVatSuffix
                    />
                  );
                }

                if (discountType === 'PRODUCT') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="2xl"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.discountedPrice}
                        size="3xl"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                      />
                    </>
                  );
                }

                if (discountType === 'PRICE_LIST') {
                  return (
                    <>
                      <FormattedPrice
                        price={product.price}
                        size="2xl"
                        lineThrough
                        color="gray"
                        productId={product.id}
                      />
                      <FormattedPrice
                        price={product.price}
                        size="3xl"
                        color="red"
                        productId={product.id}
                        showVatSuffix
                      />
                    </>
                  );
                }

                // COMPANY discount
                return (
                  <>
                    <FormattedPrice
                      price={product.price}
                      size="2xl"
                      lineThrough
                      color="gray"
                      productId={product.id}
                    />
                    <FormattedPrice
                      price={product.price}
                      size="3xl"
                      color="red"
                      categoryId={parentCategoryId}
                      productId={product.id}
                      showVatSuffix
                    />
                  </>
                );
              })()}
            </div>
            <span className="mt-3 text-sm font-normal">{product.meta.description}</span>
            <FeatureFlag feature="productDetailReviews">
              <div className="mt-3 flex items-center gap-2">
                <RatingStars rating={averageRating} starClassName="text-base" />
                <span className="text-sm font-semibold">{averageRating.toFixed(1)}</span>
                <span className="text-default-500 text-xs">
                  {reviewCount === 0
                    ? t?.productDetail.noReviewsYet
                    : tFormat(t?.productDetail.basedOnReview, { reviewsLength: reviewCount })}
                </span>
              </div>
            </FeatureFlag>
            <div className="mt-2 mb-2">
              <VariantSelector />
            </div>
            <div className={cn('mt-2 flex flex-col gap-8')}>
              <div className="flex gap-2" ref={ref}>
                <div className="flex w-full flex-col gap-4">
                  {((!isAuthenticated && features.cartForGuests) || isAuthenticated) && (
                    <>
                      <QuantityInput size="md" quantity={quantity} onChange={setQuantity} />

                      <div className="flex gap-3">
                        <Button
                          size="lg"
                          startContent={
                            isPendingUpdateMyCartProductId !== product.id && (
                              <Icon icon={faCartShopping} />
                            )
                          }
                          color="primary"
                          className="w-full @lg:w-1/2"
                          isDisabled={isStudioPreview}
                          data-testid={getTestId(ProductTestIds.PRODUCT_DETAIL_ADD_TO_CART)}
                          onPress={() => {
                            if (isStudioPreview) return;

                            const currentEventId = crypto.randomUUID();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'add_to_cart',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice * quantity,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    quantity,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            addProductToMyCart(
                              {
                                ...product,
                                quantity,
                                parentCategoryId: '',
                                discountedPriceType: 'NONE',
                              },
                              {
                                name: 'AddToCart',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          }}
                          isLoading={isPendingUpdateMyCartProductId === product.id}
                        >
                          {t?.productDetail.addToCard}
                        </Button>
                        <FeatureFlag feature="favorites">
                          <IconButton
                            aria-label="Toggle favorite"
                            size="lg"
                            variant="bordered"
                            isDisabled={isStudioPreview}
                            onClick={() => {
                              if (isStudioPreview) return;

                              if (isInFavorites) {
                                const currentEventId = crypto.randomUUID();
                                sendGTMEvent({ ecommerce: null });
                                sendGTMEvent({
                                  event: 'remove_from_favorites',
                                  event_id: currentEventId,
                                  ecommerce: {
                                    currency,
                                    value: product.discountedPrice,
                                    items: [
                                      {
                                        item_id: product.id,
                                        item_name: product.name,
                                        price: product.discountedPrice,
                                        item_category: product.superCategories?.[0]?.name || '',
                                      },
                                    ],
                                  },
                                });
                                removeProductFromFavorites(
                                  {
                                    ...product,
                                    discountedPriceType: 'NONE',
                                    parentCategoryId: '',
                                  },
                                  {
                                    name: 'RemoveFromWishlist',
                                    id: currentEventId,
                                    sourceUrl: window.location.href,
                                  },
                                );
                              } else {
                                const currentEventId = crypto.randomUUID();
                                sendGTMEvent({ ecommerce: null });
                                sendGTMEvent({
                                  event: 'add_to_favorites',
                                  event_id: currentEventId,
                                  ecommerce: {
                                    currency,
                                    value: product.discountedPrice,
                                    items: [
                                      {
                                        item_id: product.id,
                                        item_name: product.name,
                                        price: product.discountedPrice,
                                        item_category: product.superCategories?.[0]?.name || '',
                                      },
                                    ],
                                  },
                                });
                                addProductToFavorites(
                                  {
                                    ...product,
                                    discountedPriceType: 'NONE',
                                    parentCategoryId: '',
                                  },
                                  {
                                    name: 'AddToWishlist',
                                    id: currentEventId,
                                    sourceUrl: window.location.href,
                                  },
                                );
                              }
                            }}
                            isLoading={isFavoritePending(product.id)}
                          >
                            <m.div whileTap={{ scale: 1.4 }}>
                              <Icon
                                icon={isInFavorites ? faHeart : faHeartRegular}
                                className={cn('!text-base', {
                                  'text-red-600': isInFavorites,
                                })}
                              />
                            </m.div>
                          </IconButton>
                        </FeatureFlag>
                      </div>
                    </>
                  )}
                  {!((!isAuthenticated && features.cartForGuests) || isAuthenticated) && (
                    <FeatureFlag feature="favorites">
                      <IconButton
                        aria-label="Toggle favorite"
                        size="lg"
                        variant="bordered"
                        isDisabled={isStudioPreview}
                        onClick={() => {
                          if (isStudioPreview) return;

                          if (isInFavorites) {
                            const currentEventId = crypto.randomUUID();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'remove_from_favorites',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            removeProductFromFavorites(
                              {
                                ...product,
                                discountedPriceType: 'NONE',
                                parentCategoryId: '',
                              },
                              {
                                name: 'RemoveFromWishlist',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          } else {
                            const currentEventId = crypto.randomUUID();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'add_to_favorites',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            addProductToFavorites(
                              {
                                ...product,
                                discountedPriceType: 'NONE',
                                parentCategoryId: '',
                              },
                              {
                                name: 'AddToWishlist',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          }
                        }}
                        isLoading={isFavoritePending(product.id)}
                      >
                        <m.div whileTap={{ scale: 1.4 }}>
                          <Icon
                            icon={isInFavorites ? faHeart : faHeartRegular}
                            className={cn('!text-base', {
                              'text-red-600': isInFavorites,
                            })}
                          />
                        </m.div>
                      </IconButton>
                    </FeatureFlag>
                  )}
                </div>
              </div>
            </div>
            <div className={cn(shouldShowPrice ? '-mx-1 mt-8' : '-mt-2')}>
              <GeneralInfo />
            </div>
          </div>
        </div>
        <div className="mt-12">
          <ProductDetailTabs />
        </div>
        <div>
          <SimilarProducts />
        </div>
      </div>
    </LazyMotion>
  );
};

export default ProductDetail;
